import React, { useState } from 'react';
import { cleanProfanity } from '../../util/filterProfanity';

/**
 * Unified Mobile Comment Input Component
 * 
 * Optimized for iPhone/Android with consistent styling and behavior.
 * Implements best practices from MobileOptimizations.md
 */

// Optimized mobile styles from MobileOptimizations.md
const optimizedMobileTextInputStyle = {
  height: '48px',
  padding: '12px 16px',
  fontSize: '16px',
  borderRadius: '12px',
  border: '2px solid #333',
  backgroundColor: '#0a0a0a',
  color: 'white',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  lineHeight: '1.4',
  transition: 'border-color 0.2s ease, background-color 0.2s ease'
};

const focusedInputStyle = {
  ...optimizedMobileTextInputStyle,
  borderColor: '#58b047',
  backgroundColor: '#111'
};

const errorInputStyle = {
  ...optimizedMobileTextInputStyle,
  borderColor: '#ff4444',
  backgroundColor: '#1a0808'
};

const optimizedMobileButtonStyle = {
  height: '48px',
  minWidth: '80px',
  padding: '12px 20px',
  borderRadius: '12px',
  border: 'none',
  backgroundColor: '#58b047',
  color: 'black',
  fontSize: '16px',
  fontWeight: '600',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  WebkitTapHighlightColor: 'transparent',
  userSelect: 'none'
};

const disabledButtonStyle = {
  ...optimizedMobileButtonStyle,
  backgroundColor: 'red',
  color: 'black',
  cursor: 'not-allowed',
  opacity: 0.6
};

const mobileCheckboxStyle = {
  width: '20px',
  height: '20px',
  cursor: 'pointer',
  accentColor: '#58b047'
};

const checkboxLabelStyle = {
  fontSize: '14px',
  color: '#ccc',
  cursor: 'pointer',
  userSelect: 'none',
  marginLeft: '8px'
};

const UnifiedMobileCommentInput = ({
  onSubmit,
  placeholder = "Add a comment...",
  disabled = false,
  submitting = false,
  showLinkToMove = true,
  currentMoveNotation = null,
  maxLength = 1000,
  style = {},
  rateLimitError = null,
  ...props
}) => {
  const [comment, setComment] = useState('');
  const [linkToMove, setLinkToMove] = useState(false);
  const [error, setError] = useState(false);
  const [focused, setFocused] = useState(false);

  const handleSubmit = () => {
    if (!comment.trim()) {
      setError(true);
      return;
    }
    
    setError(false);
    
    // Call onSubmit with comment and optional move linking data
    onSubmit({
      content: cleanProfanity(comment.trim()),
      linkToMove: linkToMove && showLinkToMove,
      moveNotation: linkToMove ? currentMoveNotation : null
    });
    
    // Reset form
    setComment('');
    setLinkToMove(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !submitting) {
      handleSubmit();
    }
  };

  const getInputStyle = () => {
    if (error) return errorInputStyle;
    if (focused) return focusedInputStyle;
    return optimizedMobileTextInputStyle;
  };

  const getButtonStyle = () => {
    return (disabled || !comment.trim() || submitting) ? disabledButtonStyle : optimizedMobileButtonStyle;
  };

  const isSubmitDisabled = disabled || !comment.trim() || submitting;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      padding: '16px',
      ...style
    }}>
      {/* Comment input and submit button */}
      <div style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        width: '100%'
      }}>
        <input
          type="text"
          value={comment}
          onChange={(e) => {
            setError(false);
            setComment(cleanProfanity(e.target.value));
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyPress={handleKeyPress}
          maxLength={maxLength}
          placeholder={error ? "YOU MUST TYPE SOMETHING" : placeholder}
          disabled={disabled}
          style={{
            ...getInputStyle(),
            flex: 1
          }}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          {...props}
        />

        <button
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          style={getButtonStyle()}
        >
          {submitting && (
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '14px', marginRight: '6px' }}></i>
          )}
          Send
        </button>
      </div>

      {/* Rate limit error display */}
      {rateLimitError && (
        <div style={{
          color: '#ff4444',
          fontSize: '14px',
          padding: '8px 12px',
          backgroundColor: '#1a0808',
          border: '1px solid #ff4444',
          borderRadius: '8px',
          marginTop: '4px'
        }}>
          {rateLimitError}
        </div>
      )}
      
      {/* Move-linking checkbox - show when showLinkToMove is true (matches desktop behavior) */}
      {showLinkToMove && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          paddingLeft: '4px'
        }}>
          <input
            type="checkbox"
            id="unifiedLinkToMove"
            checked={linkToMove}
            onChange={(e) => setLinkToMove(e.target.checked)}
            style={mobileCheckboxStyle}
          />
          <label 
            htmlFor="unifiedLinkToMove" 
            style={checkboxLabelStyle}
          >
            Link to 
            {currentMoveNotation && (
              <span style={{ color: '#58b047', marginLeft: '6px' }}>
                {currentMoveNotation}
              </span>
            )}
          </label>
        </div>
      )}
    </div>
  );
};

export default UnifiedMobileCommentInput;